import React, { useEffect, useState } from 'react';
import { Building2, Plus, Edit2, Trash2, X, AlertCircle, Search } from 'lucide-react';
import { getBagian, createBagian, updateBagian, deleteBagian } from '../api';
import { useAuth } from '../AuthContext';

const emptyForm = { name: '', description: '' };

export default function Bagian() {
  const { hasMenuAction } = useAuth();
  const [bagianList, setBagianList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadData = () => {
    setLoading(true);
    getBagian()
      .then(res => setBagianList(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const filtered = bagianList.filter(b =>
    !search || b.name?.toLowerCase().includes(search.toLowerCase()) ||
    b.description?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({ name: item.name, description: item.description || '' });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.name) { setError('Nama bagian harus diisi'); return; }

    setSaving(true);
    try {
      if (editingItem) {
        await updateBagian(editingItem.id, form);
      } else {
        await createBagian(form);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan bagian');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus bagian "${item.name}"?`)) return;
    try {
      await deleteBagian(item.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus bagian');
    }
  };

  return (
    <div className="page bagian-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Management Bagian</h2>
            <p className="page-desc">{bagianList.length} bagian terdaftar dalam sistem</p>
          </div>
          {hasMenuAction('bagian', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} />
              <span>Tambah Bagian</span>
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Cari bagian..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Memuat data bagian...</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nama Bagian</th>
                <th>Deskripsi</th>
                <th style={{ width: 100 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id}>
                  <td>
                    <div className="cell-with-icon">
                      <Building2 size={16} className="text-muted" />
                      <strong>{b.name}</strong>
                    </div>
                  </td>
                  <td className="text-muted">{b.description || '-'}</td>
                  <td>
                    <div className="action-btns">
                      {hasMenuAction('bagian', 'edit') && (
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(b)}>
                          <Edit2 size={14} />
                        </button>
                      )}
                      {hasMenuAction('bagian', 'delete') && (
                        <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(b)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={3} className="text-center text-muted">Tidak ada data bagian</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingItem ? 'Edit Bagian' : 'Tambah Bagian Baru'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="form-error">
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Nama Bagian</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Masukkan nama bagian"
                  />
                </div>
                <div className="form-group">
                  <label>Deskripsi</label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="Masukkan deskripsi bagian (opsional)"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
